// src/context/ToolsDataContext.jsx
// Context untuk prefetch dan cache data semua tools user agar navigasi instant

import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient.js';
import { MEMORY_CACHE } from '../services/GlobalCache.js';
import CacheService from '../services/CacheService.js';

const ToolsDataContext = createContext(null);

export const useToolsData = () => useContext(ToolsDataContext);

export const ToolsDataProvider = ({ children }) => {
    const [isPrefetching, setIsPrefetching] = useState(false);
    const [prefetchProgress, setPrefetchProgress] = useState(0);
    const hasPrefetched = useRef(false);

    // Prefetch all user's tools data AND general data (Home, History, Topup, Affiliator)
    const prefetchAllTools = useCallback(async () => {
        if (hasPrefetched.current) return;

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // --- CACHE RESTORATION (Instant Load) ---
            // Try to restore from LocalStorage to MEMORY_CACHE immediately
            const restoreFromCache = () => {
                // 1. General Data
                const generalKeys = [
                    '__GENERAL_PACKAGES__',
                    '__GENERAL_VOUCHER__',
                    '__GENERAL_AFF_STATS__',
                    '__GENERAL_RUNS__',
                    '__GENERAL_TRANSACTIONS__',
                    '__GENERAL_PAYMENT_METHODS__'
                ];
                generalKeys.forEach(key => {
                    const cached = CacheService.get(key);
                    if (cached) MEMORY_CACHE[key] = cached;
                });

                // 2. We can't easily iterate all tool slugs here without knowing them, 
                // but we can try to restore "tool_*" items if we iterate storage keys,
                // OR simpler: we rely on individual pages to check CacheService if MEMORY_CACHE is empty?
                // Better: Iterate all storage keys with our prefix.
                // However, `CacheService` doesn't expose keys iteration. 
                // Let's rely on the fact that we will fetch features in step A, 
                // and we can try to load from cache *before* fetching fresh data for each feature.
            };
            restoreFromCache();

            setIsPrefetching(true);

            // --- A. PREFETCH TOOLS (Existing Logic) ---
            const prefetchTools = async () => {
                // 1. Get user's feature codes
                const { data: userFeatures } = await supabase
                    .from("user_features")
                    .select("feature_code")
                    .eq("user_id", user.id);

                if (!userFeatures || userFeatures.length === 0) return;

                const featureCodes = userFeatures.map(f => f.feature_code);

                // 2. Fetch all app_features for user's tools
                const { data: features } = await supabase
                    .from("app_features")
                    .select("*")
                    .in("slug", featureCodes)
                    .eq("is_active", true);

                if (!features || features.length === 0) return;

                const totalFeatures = features.length;
                let completed = 0;

                // 3. For each feature, fetch models and fields in parallel
                await Promise.all(features.map(async (feature) => {
                    try {
                        // Quick Restore from Disk Cache first
                        // IMPORTANT: Key must match GenericAiPage.jsx's key format
                        const cachedTool = CacheService.get(`__TOOL_${feature.slug}__`);
                        if (cachedTool) {
                            MEMORY_CACHE[feature.slug] = cachedTool;
                        }

                        // Skip fetch if data already exists in MEMORY_CACHE 
                        // (likely from GenericAiPage cold load - prevents race condition overwrite)
                        if (MEMORY_CACHE[feature.slug]?.models?.length > 0) {
                            completed++;
                            setPrefetchProgress(Math.round((completed / totalFeatures) * 100));
                            return;
                        }

                        // Fetch models for this feature
                        const { data: models } = await supabase
                            .from("ai_models")
                            .select("id,name,key,price_credits,description,created_at,configuration_options,status,is_active,video_url,label,ai_model_providers(id,name,icon_url)")
                            .eq("feature_id", feature.id)
                            .eq("is_active", true);

                        if (models && models.length > 0) {
                            // Filter and sort models
                            // Include both 'active' and 'development' - GenericAiPage will filter by role
                            const modelsData = models
                                .filter(m => ['active', 'development'].includes(m.status))
                                .sort((a, b) => {
                                    const orderA = a.configuration_options?.sort_order ?? 9999;
                                    const orderB = b.configuration_options?.sort_order ?? 9999;
                                    if (orderA !== orderB) return orderA - orderB;
                                    return new Date(b.created_at) - new Date(a.created_at);
                                });

                            // Fetch fields for all models of this feature
                            const modelIds = modelsData.map(m => m.id);
                            const { data: fields } = await supabase
                                .from("ai_model_fields")
                                .select("*")
                                .in("model_id", modelIds)
                                .order("sort_order");

                            // Build allModelFields map
                            const allModelFields = {};
                            const modelCapabilities = {};

                            modelsData.forEach(model => {
                                const mFields = (fields || []).filter(f => f.model_id === model.id);
                                allModelFields[model.id] = mFields;

                                // Extract capabilities from model config
                                const config = model.configuration_options || {};
                                modelCapabilities[model.id] = {
                                    supportsImage: config.supports_image || config.supportsImage || mFields.some(f => f.type === 'image'),
                                    supportsAudio: config.supports_audio || config.supportsAudio || mFields.some(f => f.type === 'audio'),
                                    supportsVideo: config.supports_video || config.supportsVideo || mFields.some(f => f.type === 'video'),
                                    supportsFile: config.supports_file || config.supportsFile || mFields.some(f => ['file', 'file_multi'].includes(f.type)),
                                };
                            });

                            // Populate MEMORY_CACHE for current session RAM access
                            const cacheData = {
                                featureConfig: feature,
                                models: modelsData,
                                allModelFields,
                                modelCapabilities,
                                fetchedAt: Date.now()
                            };

                            MEMORY_CACHE[feature.slug] = cacheData;

                            // Persist to LocalStorage for next visit
                            // IMPORTANT: Key must match GenericAiPage.jsx's key format
                            CacheService.set(`__TOOL_${feature.slug}__`, cacheData);
                        }

                        completed++;
                        setPrefetchProgress(Math.round((completed / totalFeatures) * 100));

                    } catch (err) {
                        console.warn(`Failed to prefetch ${feature.slug}:`, err);
                        completed++;
                    }
                }));
            };

            // --- B. PREFETCH GENERAL DATA (Optimization for Main Menu) ---
            const prefetchGeneralData = async () => {
                try {
                    // 1. Credit Packages (Topup Page)
                    const packagesPromise = supabase
                        .from("credit_packages")
                        .select("id,name,price,original_price,base_credits,populer,is_custom_button,button_text,button_url,custom_features")
                        .eq("is_active", true)
                        .order("price", { ascending: true });

                    // 2. Vouchers & Affiliate Stats (Affiliator Page)
                    const voucherPromise = supabase
                        .from('vouchers')
                        .select('*')
                        .eq('affiliator_id', user.id)
                        .limit(1);

                    const affiliateStatsPromise = supabase
                        .from('affiliate_commissions')
                        .select('commission_amount, status, created_at')
                        .eq('affiliator_id', user.id);

                    // 3. Recent History (Riwayat Page - Latest 20)
                    const runsPromise = supabase
                        .from("ai_model_runs")
                        .select("id, created_at, feature_code, model_name, credits_charged, credit_before, credit_after, result")
                        .eq("user_id", user.id)
                        .order("created_at", { ascending: false })
                        .limit(20);

                    // 4. Recent Transactions (Topup History - Latest 10)
                    const transactionsPromise = supabase
                        .from("transactions")
                        .select("*")
                        .eq("user_id", user.id)
                        .eq("status", "paid")
                        .order("paid_at", { ascending: false })
                        .limit(10);

                    const [pkgRes, voucherRes, affStatsRes, runsRes, txRes] = await Promise.all([
                        packagesPromise,
                        voucherPromise,
                        affiliateStatsPromise,
                        runsPromise,
                        transactionsPromise
                    ]);

                    // Store in MEMORY_CACHE with special keys
                    // Store in MEMORY_CACHE & LocalStorage
                    if (pkgRes.data) {
                        MEMORY_CACHE['__GENERAL_PACKAGES__'] = pkgRes.data;
                        CacheService.set('__GENERAL_PACKAGES__', pkgRes.data);
                    }
                    if (voucherRes.data) {
                        MEMORY_CACHE['__GENERAL_VOUCHER__'] = voucherRes.data[0] || null;
                        CacheService.set('__GENERAL_VOUCHER__', voucherRes.data[0] || null);
                    }
                    if (affStatsRes.data) {
                        MEMORY_CACHE['__GENERAL_AFF_STATS__'] = affStatsRes.data;
                        CacheService.set('__GENERAL_AFF_STATS__', affStatsRes.data);
                    }
                    if (runsRes.data) {
                        MEMORY_CACHE['__GENERAL_RUNS__'] = runsRes.data;
                        CacheService.set('__GENERAL_RUNS__', runsRes.data);
                    }
                    if (txRes.data) {
                        MEMORY_CACHE['__GENERAL_TRANSACTIONS__'] = txRes.data;
                        CacheService.set('__GENERAL_TRANSACTIONS__', txRes.data);
                    }

                    console.log("✅ Prefetched General Data (Topup, Affiliate, History)");

                } catch (err) {
                    console.warn("Failed to prefetch general data:", err);
                }
            };

            // Run both in parallel
            await Promise.all([prefetchTools(), prefetchGeneralData()]);

            hasPrefetched.current = true;
            console.log(`✅ Prefetched Total: ${Object.keys(MEMORY_CACHE).length} items`);

        } catch (err) {
            console.error("Prefetch error:", err);
        } finally {
            setIsPrefetching(false);
        }
    }, []);

    // Clear cache (e.g., on logout)
    const clearCache = useCallback(() => {
        Object.keys(MEMORY_CACHE).forEach(key => delete MEMORY_CACHE[key]);
        hasPrefetched.current = false;
    }, []);

    return (
        <ToolsDataContext.Provider value={{
            isPrefetching,
            prefetchProgress,
            prefetchAllTools,
            clearCache,
            hasPrefetched: hasPrefetched.current
        }}>
            {children}
        </ToolsDataContext.Provider>
    );
};

export default ToolsDataContext;
